import { useEffect } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isoWeek from 'dayjs/plugin/isoWeek';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Text, Button, useFormField, FieldChangeEvent } from '@frontend/design-system';
import { DATETIME_FORMATS } from '../../../../constants';
import { useBookingSiteStore } from '../../../../stores';
import { BOOKING_SITE_TRACKING_IDS } from '../../../../trackingIds/booking-site-tracking-ids';
import { AvailabilityDateNavigator } from './availability-date-navigator';
import { ProviderAvailability } from './provider-availability';
import { ProviderList } from './provider-list';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isoWeek);
dayjs.extend(isBetween);

interface Props {
  setAvailableOpenings: (numOfOpenings: number) => void;
}

export function ProviderSelect({ setAvailableOpenings }: Props) {
  const { t } = useTranslation('bookingSite');
  const {
    selectedAppointmentType,
    selectedAppointmentLeadTime,
    weekAnchorDate,
    setWeekAnchorDate,
    weekInView,
    setWeekInView,
    availableProviders,
    selectedProvider,
    clearSelectedProvider,
    selectedLocationTimeZone,
    isSchedularV3Enabled,
  } = useBookingSiteStore([
    'selectedAppointmentType',
    'selectedAppointmentLeadTime',
    'weekAnchorDate',
    'setWeekAnchorDate',
    'weekInView',
    'setWeekInView',
    'availableProviders',
    'selectedProvider',
    'clearSelectedProvider',
    'selectedLocationTimeZone',
    'isSchedularV3Enabled',
  ]);

  const minDate = selectedAppointmentLeadTime || dayjs().toISOString();

  const dateField = useFormField(
    {
      type: 'datePicker',
      value: weekAnchorDate,
      required: true,
    },
    [weekAnchorDate]
  );

  const handleDateChange = (event?: FieldChangeEvent) => {
    if (typeof event !== 'object') return;
    // Change event from input field. We will handle input field change on blur
    if ('target' in event && event.target && 'value' in event.target) return;
    // Change event from calendar picker
    if ('value' in event) {
      dateField.onChange(event);
      const value = event.value;
      if (dayjs(value).isValid()) {
        setWeekAnchorDate(value);
      }

      const selectedDate = isSchedularV3Enabled
        ? dayjs.tz(value, DATETIME_FORMATS.US_DATE, selectedLocationTimeZone)
        : dayjs(value, DATETIME_FORMATS.US_DATE);

      if (!selectedDate.isValid()) {
        return;
      }

      const weekStartDate = selectedDate.day(0);

      // weekStartDate can't be before selectedAppointmentLeadTime or today but considering lead time only as it will always be >= today
      if (weekStartDate.isBefore(selectedAppointmentLeadTime)) {
        setWeekInView(selectedAppointmentLeadTime);
      } else {
        setWeekInView(weekStartDate.toISOString());
      }
    }
  };

  // To automatically adjust weekAnchorDate if it goes before selectedAppointmentLeadTime
  // because we have added minDate to DateNavigator which doesn't allow selecting from calendar view
  // but its input field doesn't have that restriction
  useEffect(() => {
    if (dayjs(weekAnchorDate).isBefore(selectedAppointmentLeadTime)) {
      setWeekAnchorDate(selectedAppointmentLeadTime);
    }
  }, [weekAnchorDate, selectedAppointmentLeadTime, setWeekAnchorDate]);

  if (selectedProvider && selectedAppointmentType) {
    return (
      <>
        <Button
          variant='tertiary'
          iconName='caret-left'
          size='small'
          css={{ margin: theme.spacing(2, 0, 4, 0) }}
          trackingId={BOOKING_SITE_TRACKING_IDS.providerStep.backToProviderListButton}
          onClick={() => {
            clearSelectedProvider();
            setAvailableOpenings(0);
          }}
        >
          {t('Back to provider list')}
        </Button>
        <ProviderAvailability setAvailableOpenings={setAvailableOpenings} />
      </>
    );
  }

  if (!availableProviders?.length) {
    return (
      <Text as='h3' color='light' className='left'>
        {t('No Providers Available for your selected appointment type')}
      </Text>
    );
  }

  return (
    <div>
      <Text>
        {t(
          'Browse through the list of providers and check their upcoming appointment availability with just a glance. If you need more details, like specific time slots, just click on their name in the table.'
        )}
      </Text>
      <div css={navSectionStyle}>
        <AvailabilityDateNavigator
          weekInView={weekInView}
          minDate={minDate}
          handleDateChange={handleDateChange}
          dateFieldProps={dateField}
          isSchedularV3Enabled={isSchedularV3Enabled}
          selectedLocationTimeZone={selectedLocationTimeZone}
        />
      </div>
      <div css={{ maxHeight: 380, overflow: 'auto' }}>
        <ProviderList />
      </div>
    </div>
  );
}

const navSectionStyle = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  borderBottom: `1px solid ${theme.colors.neutral20}`,
  p: { margin: 0 },
});
